import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Lock, ArrowLeft } from 'lucide-react';
import { Input, Label } from '../components/ui/Form';
import { Button } from '../components/ui/Button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const [adminKey, setAdminKey] = useState(localStorage.getItem('asana_admin_key') || '');
  const [authed, setAuthed] = useState(false);
  const [tab, setTab] = useState('bookings');
  const [bookings, setBookings] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [stats, setStats] = useState({ bookings: 0, inquiries: 0 });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const headers = { 'X-Admin-Key': adminKey };

  const loadAll = async (key) => {
    setLoading(true);
    setError('');
    try {
      const [b, i, s] = await Promise.all([
        axios.get(`${API}/bookings`, { headers: { 'X-Admin-Key': key } }),
        axios.get(`${API}/inquiries`, { headers: { 'X-Admin-Key': key } }),
        axios.get(`${API}/stats`, { headers: { 'X-Admin-Key': key } }),
      ]);
      setBookings(b.data);
      setInquiries(i.data);
      setStats(s.data);
      setAuthed(true);
      localStorage.setItem('asana_admin_key', key);
    } catch (err) {
      setError('Invalid admin key or server error.');
      setAuthed(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (adminKey) loadAll(adminKey);
    // eslint-disable-next-line
  }, []);

  if (!authed) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center p-6">
        <div className="max-w-md w-full rounded-3xl bg-offwhite border border-moss/25 p-10">
          <div className="flex items-center gap-3 mb-2">
            <Lock className="w-5 h-5 text-pond" />
            <p className="overline text-muted">Admin login</p>
          </div>
          <h1 className="editorial-display text-pond text-3xl md:text-4xl leading-tight mb-6">
            Asana Inbox
          </h1>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              loadAll(adminKey);
            }}
            className="space-y-6"
          >
            <div>
              <Label htmlFor="key">Admin Key</Label>
              <Input
                id="key"
                type="password"
                value={adminKey}
                onChange={(e) => setAdminKey(e.target.value)}
                placeholder="••••••••"
                data-testid="admin-key-input"
                required
              />
            </div>
            {error && <p className="text-sm text-red-700" data-testid="admin-error">{error}</p>}
            <Button type="submit" size="lg" disabled={loading} className="w-full" data-testid="admin-login-btn">
              {loading ? 'Verifying…' : 'Enter'}
            </Button>
          </form>
          <Link to="/" className="inline-flex items-center gap-2 mt-6 text-sm text-muted hover:text-pond">
            <ArrowLeft className="w-4 h-4" /> Back to site
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand">
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-12 md:py-16">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <p className="overline text-muted mb-3">Asana Admin · v1</p>
            <h1 className="editorial-display text-pond text-4xl md:text-5xl leading-tight">
              Inbox
            </h1>
          </div>
          <div className="flex gap-3">
            <Link to="/" className="text-sm text-muted hover:text-pond inline-flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" /> Site
            </Link>
            <button
              onClick={() => {
                localStorage.removeItem('asana_admin_key');
                setAdminKey('');
                setAuthed(false);
              }}
              className="text-sm text-muted hover:text-red-700"
              data-testid="admin-logout"
            >
              Sign out
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 md:gap-6 mb-10">
          <div className="rounded-2xl bg-offwhite border border-moss/25 p-6">
            <p className="overline text-muted mb-2">Bookings</p>
            <p className="editorial-display text-pond text-5xl">{stats.bookings}</p>
          </div>
          <div className="rounded-2xl bg-offwhite border border-moss/25 p-6">
            <p className="overline text-muted mb-2">Inquiries</p>
            <p className="editorial-display text-pond text-5xl">{stats.inquiries}</p>
          </div>
        </div>

        <div className="flex gap-2 mb-6">
          {['bookings', 'inquiries'].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              data-testid={`admin-tab-${t}`}
              className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all ${
                tab === t
                  ? 'bg-pond text-offwhite'
                  : 'bg-offwhite text-charcoal border border-moss/25 hover:border-pond'
              }`}
            >
              {t === 'bookings' ? `Bookings (${bookings.length})` : `Inquiries (${inquiries.length})`}
            </button>
          ))}
        </div>

        {tab === 'bookings' && (
          <div className="space-y-3" data-testid="admin-bookings-list">
            {bookings.length === 0 && (
              <p className="text-muted text-sm">Walang booking pa.</p>
            )}
            {bookings.map((b) => (
              <div
                key={b.id}
                className="bg-offwhite rounded-2xl border border-moss/25 p-5 md:p-6 grid grid-cols-1 md:grid-cols-12 gap-4"
              >
                <div className="md:col-span-3">
                  <p className="overline text-muted mb-1">{b.experience_type}</p>
                  <p className="font-display text-pond text-2xl leading-tight">{b.full_name}</p>
                  <p className="text-xs text-muted mt-1">
                    {new Date(b.created_at).toLocaleString()}
                  </p>
                </div>
                <div className="md:col-span-3 text-sm text-charcoal/80">
                  <p>{b.email}</p>
                  <p>{b.phone}</p>
                </div>
                <div className="md:col-span-3 text-sm text-charcoal/80">
                  <p className="font-medium text-pond">{b.visit_date}</p>
                  <p>{b.timeslot}</p>
                  <p>{b.guests} pax</p>
                </div>
                <div className="md:col-span-3 text-sm">
                  {b.add_ons?.length > 0 && (
                    <p className="text-muted">
                      <span className="overline">Add-ons:</span><br/>
                      {b.add_ons.join(', ')}
                    </p>
                  )}
                  {b.notes && <p className="mt-2 text-charcoal/70 italic">"{b.notes}"</p>}
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === 'inquiries' && (
          <div className="space-y-3" data-testid="admin-inquiries-list">
            {inquiries.length === 0 && (
              <p className="text-muted text-sm">Walang inquiry pa.</p>
            )}
            {inquiries.map((q) => (
              <div
                key={q.id}
                className="bg-offwhite rounded-2xl border border-moss/25 p-5 md:p-6"
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-3">
                  <div>
                    <p className="overline text-muted mb-1">{q.subject}</p>
                    <p className="font-display text-pond text-2xl leading-tight">{q.name}</p>
                  </div>
                  <p className="text-xs text-muted">{new Date(q.created_at).toLocaleString()}</p>
                </div>
                <p className="text-sm text-muted mb-3">
                  {q.email} {q.phone && `· ${q.phone}`}
                </p>
                <p className="text-charcoal/85 leading-relaxed">{q.message}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
